/* prettier-ignore */
import { CertificateState, RIASRecordType, SubscriptionState, RIASRoles } from "~/lib/types";
import { padId, schemeData, Subscription } from "~/lib";
/* prettier-ignore */
import { updateDB, putItem,	getProfile,	getByUser,	RIASTABLE,	runQueryCommand, getCounter } from "~/_server/dynamoDB";
import { sendEmail, sendDevEmail } from "~/_server/server.email";
import { SEND_TO_ADMINS, SEND_TO_DEV } from "~/lib/constants";
import { createInvoice, getInvoiceUrl } from "~/_server/accounts";
import { getByRowTypeServer } from "~/_server/dynamoQueries";

const getSecondsOneYearLater = (seconds: number) => {
	const date = new Date(seconds * 1000);
	date.setFullYear(date.getFullYear() + 1);
	return Math.floor(date.getTime() / 1000);
};

export const paymentRecieved = async (invoiceId: string) => {
	"use server";
	console.log( "Payment received for invoice", invoiceId );
	
	let emailTemplate = null, emailProps = {};

	const r = await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiInvoiceId",
		KeyConditionExpression: "invoiceId = :id",
		ExpressionAttributeValues: { ":id": invoiceId },
	});

	console.log( "Matched invoice:", r );
	
	if (r?.items?.[0]) {
		const item = r.items[0];
		const recordType = item?.recordType;

		switch (recordType) {
			case "CERTIFICATE":
				await updateDB(item.pk, item.sk, { status: CertificateState.PAID });
				emailTemplate = "certificate-paid";
				break;

			case "SUBSCRIPTION":
				let update: { status?: string; expires?: number; joined?: number } = {};

				switch (item.status) {
					case SubscriptionState.ACTIVE:
					case SubscriptionState.RENEWING:
						update.status = SubscriptionState.ACTIVE;
						update.expires = getSecondsOneYearLater(item.expires);
						break;

					case SubscriptionState.SUSPENDED:
					case SubscriptionState.ASSESSING:
					case SubscriptionState.PENDING_BODY:
					case SubscriptionState.PRACTICAL:
						update.expires = getSecondsOneYearLater(item.expires);
						break;

					case SubscriptionState.PENDING:
					case SubscriptionState.APPLYING:
						// They become a certifier now (OK to re-purpose SubscriptionState.ACTIVE - it's just a string)
						await updateDB(
							item.pk,
							padId(parseInt(item.pk.split("#")[1]), RIASRecordType.USER),
							{ role: RIASRoles.CERTIFIER, status: SubscriptionState.ACTIVE },
						);

						const now = Math.floor(Date.now() / 1000);
						update.status = SubscriptionState.ASSESSING;
						update.expires = getSecondsOneYearLater(now);
						update.joined = now;

						emailTemplate = "subscription-complete";
						break;
				}

				if (Object.keys(update).length) {
					await updateDB(item.pk, item.sk, update);
				}

				if ( emailTemplate ) {
					try {
						const user = await getProfile( parseInt( item.pk.split( "#" )[1] ) );
						const scheme = schemeData( item.scheme );
					
						let invoiceLink = await getInvoiceUrl( invoiceId );
					
						if ( !( "error" in invoiceLink ) ) {
							emailProps.schemeName = scheme.name;

							await sendEmail( {
								templateName: emailTemplate,
								props: {
									name: user.firstName,
									schemeName: scheme,
									invoiceLink: invoiceLink.onlineInvoiceUrl,
								},
								to: user.email,
								bcc: SEND_TO_DEV,
							} );
						}
					} catch ( err ) {
						await sendDevEmail( "Error sending email after payment", JSON.stringify( err ) );			
					}
				}
				break;
		}
	} else {
		await sendDevEmail(
			"Payment received but no matching record",
			`Invoice ID: ${invoiceId}`,
		);
	}
};

export const verifyUser = async (userId: number) => {
	"use server";

	try {
		// Set user record to certifier
		await updateDB(`USER#${userId}`, padId(userId, RIASRecordType.USER), {
			status: "payment-pending",
			role: RIASRoles.CERTIFIER,
		});

		const subscriptions = await getByUser( "SUBSCRIPTION", userId, -1 ).then( r => r.items ??[] ) as Subscription[];
		
		if ( subscriptions.length === 0 ) {
			const user = await getProfile( userId );

			const name = await generateSubscriptionName( user.targetScheme );

			const newSub: Subscription = {
				pk: `USER#${userId}`,
				sk: padId( parseInt( name.replace( "SUBSCRIPTION#", "" ), 10 ), RIASRecordType.SUBSCRIPTION ),
				status: SubscriptionState.APPLYING,
				expires: 0,
				recordType: RIASRecordType.SUBSCRIPTION,
				name,
				scheme: user.targetScheme,
			}

			await putItem( newSub );

			subscriptions.push( newSub );
		}

		for(  const sub of subscriptions){
				if (
					sub.status == SubscriptionState.APPLYING ||
					sub.status == SubscriptionState.VALIDATING
				) {
					try {
						// Create an invoice for joining and the first year's subs
						const invoiceId = await createSubscriptionInvoice(
							sub.scheme,
							parseInt(sub.sk?.replace("SUBSCRIPTION#", "") as string, 10),
							userId,
							true,
							new Date().getFullYear(),
						);

						if (invoiceId) {
							// Set subscription to paying
							await updateDB(sub.pk as string, sub.sk as string, {
								expires: 0,
								status: SubscriptionState.PENDING,
								invoiceId,
							});

							const user = await getProfile(userId);
							const scheme = schemeData(sub.scheme);

							// Send an email to the user with next steps (payment)
							await sendEmail({
								templateName: "application-verified",
								props: {
									user: { name: user.firstName },
									schemeName: scheme.name,
									login: "https://rias-regs.co.uk/login",
								},
								to: user.email,
								bcc: SEND_TO_ADMINS,
							});
						}
					} catch (err) {
						console.log("Error verifying user", err);
					}
				}
			}
		

		return true;
	} catch (err) {
		console.log("Error verifying user", err);
	}

	return false;
};

export const createSubscriptionInvoice = async (
	schemeSlug: string,
	riasId: number,
	userId: number,
	incJoiningFee: boolean = false,
	year: number = new Date().getFullYear(),
) => {
	try {
		const user = await getProfile(userId);
		const scheme = schemeData(schemeSlug);

		const lineItems = [];

		if (incJoiningFee) {
			lineItems.push({
				description: `Joining Fee - Certification of Design ${scheme.name}) (#${riasId})`,
				quantity: 1.0,
				unitAmount: scheme.joiningFee,
				accountCode: "203",
				lineAmount: scheme.joiningFee,
			});
		}

		lineItems.push({
			description: `Subscription Fee (${year}) - Certification of Design ${scheme.name}) (#${riasId})`,
			quantity: 1.0,
			unitAmount: scheme.renewalFee,
			accountCode: "201",
			lineAmount: scheme.renewalFee,
		});

		const invoiceId = await createInvoice(
			user.email,
			`${user.firstName} ${user.lastName}`,
			lineItems,
			riasId,
		);

		return invoiceId;
	} catch (err) {
		console.log("Error creating subscription invoice", err);
	}

	return null;
};


export const matchABExpiry = async () => {
	"use server";

	const subs = await getByRowTypeServer(
		RIASRecordType.SUBSCRIPTION,
		2000,
		"#expires,#approvedBodyId",
		{ "#expires": "expires", "#approvedBodyId": "approvedBodyId" },
	);

	const abs = await getByRowTypeServer(
		RIASRecordType.APPROVEDBODY,
		2000,
		"#expires,#sk,#pk",
		{ "#expires": "expires", "#sk": "sk", "#pk": "pk" },
	);

	const byPk = new Map(
		abs?.items.map( item => [
			parseInt( item.pk.replace( "APPROVEDBODY#", "" ) ),
			item.expires,
		] ) ?? [],
	);

	const result = subs.items.reduce( ( acc, item ) => {
		const { approvedBodyId, expires } = item;

		if (
			expires &&
			approvedBodyId &&
			( !byPk.has( parseInt( approvedBodyId ) ) ||
				expires > byPk.get( parseInt( approvedBodyId ) ) )
		) {
			if ( !acc[approvedBodyId] || expires > acc[approvedBodyId] ) {
				acc[approvedBodyId] = expires;
			}
		}
		return acc;
	}, {} );

	for ( const [approvedBodyId, expires] of Object.entries( result ) ) {
		const pk = `APPROVEDBODY#${approvedBodyId}`;
		const sk = `APPROVEDBODY#${approvedBodyId.toString().padStart( 7, "0" )}`;

		await updateDB( pk, sk, { expires } );
	}

	return;
}


const generateSubscriptionName = async ( schemeSlug: string ) => {
	"use server";
	
	const scheme = schemeData(schemeSlug);

	if ( !scheme ) {
		throw new Error( "Scheme not found: " + schemeSlug );
	}	
	
	const subId = await getCounter( "SUBSCRIPTION" );

	if ( subId < 1 ) {
		throw new Error( "Error generating subscriptionId" );
	}

	return `${scheme.prefix}-D-${padId( subId, RIASRecordType.SUBSCRIPTION )}`;

}